import React, { useMemo } from 'react';
import { 
  Modal, 
  Box, 
  SpaceBetween, 
  ProgressBar, 
  Button, 
  StatusIndicator,
  Container,
  Header,
} from '@cloudscape-design/components';
import { useTranslation } from 'react-i18next';

/**
 * Modal que muestra el progreso de carga de archivos de audio
 */
const AudioUploadProgressModal = ({ 
  visible, 
  onDismiss, 
  uploadItems = [], 
  progressData = {},
  queueInfo = { queueSize: 0, queuedFiles: [] }
}) => {
  const { t } = useTranslation();
  
  // Calcular información de progreso general
  const progressInfo = useMemo(() => {
    // Valor predeterminado
    let result = {
      overallProgress: 0,
      completedCount: 0,
      itemsWithProgress: 0,
      totalProgress: 0
    };
    
    if (uploadItems.length === 0) return result;
    
    // Contar archivos completados
    const completedCount = uploadItems.filter(item => 
      progressData[item.label]?.status === 'completed'
    ).length;
    
    // Calcular progreso total
    let totalProgress = 0;
    let itemsWithProgress = 0;
    
    uploadItems.forEach(item => {
      const progress = progressData[item.label]?.progress || 0;
      if (progress > 0) {
        totalProgress += progress;
        itemsWithProgress++;
      }
    });
    
    const overallProgress = itemsWithProgress > 0 
      ? Math.round(totalProgress / itemsWithProgress) 
      : 0;
    
    return {
      overallProgress,
      completedCount,
      itemsWithProgress,
      totalProgress
    };
  }, [uploadItems, progressData]);
  
  // Convertir estado a tipo de indicador
  const getStatusType = (filename) => {
    const fileData = progressData[filename];
    if (!fileData) return 'pending';
    
    switch(fileData.status) {
      case 'uploading': return 'in-progress';
      case 'processing': 
      case 'transcribing': 
      case 'analyzing': return 'in-progress';
      case 'completed': return 'success';
      case 'error': return 'error';
      default: return 'in-progress'; 
    }
  };

  // Convertir estado a texto descriptivo
  const getStatusText = (filename) => {
    const fileData = progressData[filename];
    if (!fileData) return t('upload.pending', 'Pendiente');
    
    switch(fileData.status) {
      case 'uploading': return t('upload.uploading', 'Subiendo');
      case 'processing': return t('upload.processing', 'Procesando');
      case 'transcribing': return t('upload.processing', 'Procesando');
      case 'analyzing': return t('upload.processing', 'Procesando');
      case 'completed': return t('upload.completed', 'Completado');
      case 'error': return t('upload.uploadFailed', 'Error');
      default: return t('upload.pending', 'Pendiente');
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header={t('upload.progressTitle', 'Progreso de carga de Audios')}
      footer={
        <Box float="right">
          <Button onClick={onDismiss} variant="primary">
            {t('upload.close', 'Cerrar')}
          </Button>
        </Box>
      }
      size="medium"
    >
      <SpaceBetween size="l">
        {/* Sección de progreso general */}
        <Container
          header={<Header variant="h3">{t('upload.overallProgress', 'Progreso general')}</Header>}
        >
          <ProgressBar 
            value={progressInfo.overallProgress} 
            label={`${progressInfo.overallProgress}%`}
            description={
              <SpaceBetween direction="vertical" size="xs">
                <div>
                  {`${progressInfo.completedCount} ${t('upload.of', 'de')} ${uploadItems.length + queueInfo.queueSize} ${t('upload.filesCompleted', 'archivos completados')}`}
                </div>
                {queueInfo.queueSize > 0 && (
                  <div>{t('upload.queuedFiles', 'Archivos en cola')}: {queueInfo.queueSize}</div>
                )}
              </SpaceBetween>
            }
          />
        </Container>
        
        {/* Sección de información de cola */}
        {queueInfo.queueSize > 0 && queueInfo.queuedFiles.length > 0 && (
          <Container header={<Header variant="h3">{t('upload.queuedFiles', 'Archivos en cola')}</Header>}>
            <SpaceBetween size="m">
              {queueInfo.queuedFiles.map((item, index) => (
                <Box key={`queue-${index}`} padding="s">
                  <SpaceBetween size="s">
                    <div>
                      <strong>{item.fileId || `${t('upload.file', 'Archivo')} ${index + 1}`}</strong>
                      <div>
                        <StatusIndicator type="pending">
                          {t('upload.queued', 'En cola')}
                        </StatusIndicator>
                      </div>
                    </div>
                    <ProgressBar 
                      value={0} 
                      label={t('upload.waiting', 'En espera')}
                    />
                  </SpaceBetween>
                </Box>
              ))}
              
              {queueInfo.queueSize > queueInfo.queuedFiles.length && (
                <Box textAlign="center">
                  {`+ ${queueInfo.queueSize - queueInfo.queuedFiles.length} ${t('upload.additionalQueuedFiles', 'archivos adicionales en cola')}`}
                </Box>
              )}
            </SpaceBetween>
          </Container>
        )}
        
        {/* Sección de archivos en procesamiento */}
        <Container header={<Header variant="h3">{t('upload.files', 'Archivos')}</Header>}>
          <SpaceBetween size="m">
            {uploadItems.map((item, index) => {
              const fileData = progressData[item.label] || {};
              const fileProgress = fileData.progress || 0;
              const fileStatus = fileData.status || 'pending';
              const fileError = fileData.error || '';
              
              return (
                <Box key={index} padding="s">
                  <SpaceBetween size="s">
                    <div>
                      <strong>{item.fileId}</strong>
                      <div>
                        <StatusIndicator type={getStatusType(item.fileId)}>
                          {getStatusText(item.label)}
                        </StatusIndicator>
                      </div>
                    </div>
                    <ProgressBar 
                      value={fileProgress} 
                      label={`${fileProgress}%`}
                      status={fileStatus === 'error' && fileError ? 'error' : undefined}
                      additionalInfo={fileStatus === 'error' && fileError ? fileError : undefined}
                    />
                  </SpaceBetween>
                </Box>
              );
            })}
            
            {uploadItems.length === 0 && (
              <Box textAlign="center">
                {t('upload.noActiveUploads', 'No hay cargas activas')}
              </Box>
            )}
          </SpaceBetween>
        </Container>
      </SpaceBetween>
    </Modal>
  );
};

export default AudioUploadProgressModal;